import { CompletionPrompts, mergeAiConfigs } from '@tunasong/ai-lib'
import type { AIConfig, ChatMessage } from '@tunasong/ai-lib'
import { z } from 'zod'
import type { Tool } from '../types.js'

export interface GenerateImageDeps {
  /**
   * Queue an image task — creates the pending entity stub + SQS post.
   * Shared with the legacy /image HTTP endpoint so behavior is identical.
   */
  queueImageTask: (input: {
    prompt: ChatMessage[]
    name: string
    parentId: string
    userId: string
    chatModel?: string
    /**
     * Client-reserved entity id. When set, the created image entity uses this
     * exact id so the client's optimistic placeholder ref resolves without
     * stream observation. When absent, the queue mints a server id.
     */
    entityId?: string
  }) => Promise<{ entityId: string; name: string }>
  loadUserAiConfig: (userId: string) => Promise<AIConfig | undefined>
  loadSpaceAiConfig: (focusEntityId: string) => Promise<AIConfig | undefined>
}

const schema = z.object({
  prompt: z
    .string()
    .min(1)
    .describe(
      'Image prompt — describe the image to generate. Anchor it in the song you can read from your context.'
    ),
  name: z.string().min(1).describe('Display name for the image entity').optional(),
})

export const createGenerateImageTool = (
  deps: GenerateImageDeps
): Tool<z.infer<typeof schema>, { entityId: string; name: string }> => ({
  name: 'generate_image',
  // The handler only enqueues an async SQS task and returns immediately; the
  // image entity is created `pending: true` with no URL. Without this guard a
  // model that reads the queued result as "not done" re-calls generate_image
  // every step (up to maxSteps) and spawns a pile of duplicate pending images.
  // The runtime short-circuits the 2nd+ call in the same turn.
  oncePerTurn: true,
  description: [
    'Generate an image and attach it as a child entity of the current focus (typically the song).',
    'Use when the user asks for an album cover, illustration, or visual representation of the song.',
    'Build the prompt from the song you already have in context — read the lyrics, mood, and any stated genre/style before constructing the prompt.',
    "You do NOT need to mention the user's saved image style — the tool layer enforces that automatically.",
  ].join(' '),
  schema,
  handler: async (args, ctx) => {
    const focusEntityId = ctx.focus?.entityId ?? ctx.parentEntityId
    if (!focusEntityId) {
      return { ok: false, error: 'no-focus' }
    }
    try {
      const [userConfig, spaceConfig] = await Promise.all([
        deps.loadUserAiConfig(ctx.userId),
        deps.loadSpaceAiConfig(focusEntityId),
      ])
      const merged = mergeAiConfigs(spaceConfig, userConfig) as AIConfig | undefined
      // CompletionPrompts.songImage prepends system messages from
      // merged.contexts['song:image'] (imageStyle, personality, …) exactly
      // the way the browser-side getImagePrompt does today.
      const promptSpec = CompletionPrompts.songImage({
        title: '',
        lyrics: '',
        userConfig: merged,
        promptText: args.prompt,
      })
      const queued = await deps.queueImageTask({
        prompt: promptSpec.prompt as unknown as ChatMessage[],
        name: args.name ?? 'AI image',
        parentId: focusEntityId,
        userId: ctx.userId,
        chatModel: promptSpec.model,
        // Bind the created entity to the client-reserved id when present so the
        // browser placeholder ref resolves with no stream observation.
        entityId: ctx.reservedImageEntityId,
      })
      return { ok: true, result: queued }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      return { ok: false, error: message }
    }
  },
})
